import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiMapPin, FiCalendar, FiUsers, FiAlertCircle } from 'react-icons/fi';
import Api from '../../../Api.js';
import './PopularWeddingPackages.css';

const SKELETON_COUNT = 3;

/* ---------- Helpers ---------- */
const formatINR = (value) => {
  const num = Number(value);
  if (!num || num <= 0 || Number.isNaN(num)) return null;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `₹${num}`;
  }
};

const buildImage = (url) =>
  url?.startsWith('http')
    ? url
    : `https://admintest.jayrajgroup.org${url || ''}`;

export default function PopularWeddingPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchPackages = async () => {
      try {
        setLoading(true);
        const response = await Api.get('/wedding/packages/popular', {
          signal: abortController.signal,
        });

        const payload = response.data || response;

        if (payload.status && Array.isArray(payload.data) && payload.data.length > 0) {
          const mappedPackages = payload.data.map((item) => {
            const mealPlan = item.meal_plans?.[0];
            const pricePerPerson = mealPlan?.price_per_person
              ? parseFloat(mealPlan.price_per_person)
              : 0;

            return {
              id: item.id,
              name: item.name,
              slug: item.slug,
              shortDescription: item.short_description,
              image: buildImage(item.featured_photo_url),
              duration: `${item.duration_days}D / ${item.duration_nights}N`,
              destination: item.destination?.name || 'Destination',
              link: `/destination-wedding-packages-details/${item.slug}`,
              pricePerPerson,
              formattedPrice: formatINR(pricePerPerson),
            };
          });
          setPackages(mappedPackages);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          console.error('Failed to fetch popular wedding packages:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
    return () => abortController.abort();
  }, []);

  return (
    <section className="pw-section">
      <span className="pw-orb pw-orb-a" aria-hidden="true" />
      <span className="pw-orb pw-orb-b" aria-hidden="true" />

      <div className="pw-shell">
        {/* ---------- Header ---------- */}
        <motion.header
          className="pw-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="pw-title">Popular Wedding Packages</h2>
          <p className="pw-subtitle">Curated for your dream celebration</p>
        </motion.header>

        {/* ---------- Body ---------- */}
        {error ? (
          <div className="pw-empty pw-empty-error">
            <span className="pw-empty-icon">
              <FiAlertCircle />
            </span>
            <h3 className="pw-empty-title">Couldn’t load packages</h3>
            <p className="pw-empty-text">Please try again in a moment.</p>
          </div>
        ) : loading ? (
          <div className="pw-grid" aria-busy="true" aria-label="Loading packages">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <div key={i} className="pw-skeleton" />
            ))}
          </div>
        ) : packages.length === 0 ? (
          <div className="pw-empty">
            <span className="pw-empty-icon">
              <FiAlertCircle />
            </span>
            <h3 className="pw-empty-title">No packages yet</h3>
            <p className="pw-empty-text">New experiences are being added soon.</p>
          </div>
        ) : (
          <div className="pw-grid">
            {packages.map((pkg, index) => (
              <motion.article
                key={pkg.id || pkg.name || index}
                className="pw-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: Math.min(index, 8) * 0.08 }}
              >
                <a href={pkg.link} className="pw-card-link">
                  <div className="pw-card-media">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="pw-card-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src =
                          'https://via.placeholder.com/800x600?text=No+Image';
                      }}
                    />
                    <span className="pw-card-veil" aria-hidden="true" />

                    {/* Duration badge — top-left */}
                    <span className="pw-badge pw-badge-duration">
                      <FiCalendar aria-hidden="true" />
                      {pkg.duration}
                    </span>

                    {/* Price badge — top-right */}
                    {pkg.formattedPrice && (
                      <span className="pw-badge pw-badge-price">
                        <FiUsers aria-hidden="true" />
                        <span className="pw-badge-price-value">
                          {pkg.formattedPrice}
                        </span>
                        <span className="pw-badge-price-suffix">/ person</span>
                      </span>
                    )}

                    {/* Body overlay */}
                    <div className="pw-card-body">
                      {pkg.destination && (
                        <span className="pw-card-loc">
                          <FiMapPin aria-hidden="true" />
                          {pkg.destination}
                        </span>
                      )}

                      <h3 className="pw-card-title">{pkg.name}</h3>

                      {pkg.shortDescription && (
                        <p className="pw-card-desc">{pkg.shortDescription}</p>
                      )}

                      <span className="pw-card-cta">
                        <span className="pw-card-cta-text">Explore Package</span>
                        <FiArrowRight className="pw-card-cta-icon" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </a>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}